import React, { useRef, useState, useEffect } from 'react';
import { Star } from '@material-ui/icons';
import { FolioFolder, VelocityEngageFolioFolderDocument } from 'models';

import { TabProps } from 'components/shared/Tabs/Tabs';
import strings from 'common/strings';
import LoadingIndicator from 'components/shared/LoadingIndicator';
import Alert from 'components/shared/Alert';

import MediaContainer from './MediaContainer';
import {
  useFolio,
  useFeatureFolioDocument,
  useUnfeatureFolioDocument,
  useShowFolioDocument,
  useHideFolioDocument,
  useReorderFolioDocuments,
  useDeleteFolioDocument,
  useUpdateFolioDocumentName,
} from 'api';

import './VelocityEngageMediaTab.scss';
import CarfaxModalProvider from './CarfaxModalProvider';

const MAX_FEATURED_DOCUMENTS = 4;

var Divider = () => {
  return <div className="Divider" />;
};

interface VelocityEngageMediaTabProps extends TabProps {
  vehicleId: string;
}

const VelocityEngageMediaTab: React.FC<VelocityEngageMediaTabProps> = ({
  vehicleId,
}) => {
  const { data, isLoading } = useFolio(vehicleId);
  const { mutate: featureFolioDocument } = useFeatureFolioDocument();
  const { mutate: unfeatureFolioDocument } = useUnfeatureFolioDocument();
  const { mutate: showFolioDocument } = useShowFolioDocument();
  const { mutate: hideFolioDocument } = useHideFolioDocument();
  const { mutate: reorderFolioDocuments } = useReorderFolioDocuments();
  const { mutate: deleteFolioDocument } = useDeleteFolioDocument();
  const { mutate: updateFolioDocumentName } = useUpdateFolioDocumentName();
  const [folioFolders, setFolioFolders] = useState<FolioFolder[]>([]);
  const [error, setError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastFolderIndex = folioFolders.length - 1;
  useEffect(() => {
    if (data?.data?.folders) {
      setFolioFolders(data?.data?.folders);
    }
  }, [data]);

  const featuredDocumentsLength = () => {
    const featuredDocuments = folioFolders[0];
    return featuredDocuments ? featuredDocuments.documents.length : 0;
  };

  const onFeatureDocumentClick = async (
    document: VelocityEngageFolioFolderDocument
  ) => {
    if (
      featuredDocumentsLength() === MAX_FEATURED_DOCUMENTS &&
      !document.featured
    ) {
      setError(strings.TOO_MANY_FEATURED_DOCS);
      return;
    }
    const originalFeatured = document.featured;
    document.featured = !document.featured;
    if (originalFeatured) {
      await unfeatureFolioDocument({ vehicleId, document });
    } else {
      await featureFolioDocument({ vehicleId, document });
    }
  };

  const onPublishDocument = async (
    document: VelocityEngageFolioFolderDocument
  ) => {
    const originalVisibility = document.visible;
    document.visible = !document.visible;
    if (originalVisibility) {
      await hideFolioDocument({ vehicleId, document });
    } else {
      await showFolioDocument({ vehicleId, document });
    }
  };

  const onDeleteDocument = async (
    document: VelocityEngageFolioFolderDocument
  ) => {
    await deleteFolioDocument({ vehicleId, document });
  };

  const onEditDocument = async (
    document: VelocityEngageFolioFolderDocument,
    title: string
  ) => {
    await updateFolioDocumentName({ vehicleId, document, name: title });
  };

  const reorderDocument = async (
    folderId: string,
    reorderObject: Record<string, number>
  ) => {
    await reorderFolioDocuments({
      vehicleId,
      reorderObject,
    });
  };

  const handleClose = () => setError('');

  return (
    <CarfaxModalProvider>
      <div className="VelocityEngageMediaTab" ref={scrollRef}>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          folioFolders.map((folder: FolioFolder, idx: number) => (
            <>
              <MediaContainer
                folderId={folder.id}
                vehicleId={vehicleId}
                icon={
                  folder.displayLabel === strings.FEATURED ? (
                    <Star className="Featured-icon" />
                  ) : null
                }
                title={folder.displayLabel}
                media={folder.documents}
                scrollRef={scrollRef}
                onStar={onFeatureDocumentClick}
                onPublish={onPublishDocument}
                onDelete={onDeleteDocument}
                onEdit={onEditDocument}
                onReorder={reorderDocument}
              />
              {idx !== lastFolderIndex && <Divider />}
            </>
          ))
        )}
        <Alert
          open={!!error}
          duration={3000}
          handleClose={handleClose}
          className="VelocityEngageMediaTab-alert"
          contentProps={{
            variant: 'error',
            message: error,
            onClose: handleClose,
          }}
        />
      </div>
    </CarfaxModalProvider>
  );
};

export default VelocityEngageMediaTab;
